import React from "react";

export default function TermsConditions() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 text-gray-300">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Terms & Conditions</h1>
        <p className="text-gray-400 text-sm">Effective Date: June 15, 2026 &nbsp;|&nbsp; Last Updated: June 15, 2026</p>
        <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full mx-auto mt-4" />
      </div>

      <div className="space-y-10 text-sm md:text-base leading-relaxed">

        {/* Section 1 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">1.</span> Agreement to Terms
          </h2>
          <p>By accessing or using <strong className="text-white">Kronos AI</strong> (the "Service"), operated by DevX House at <a href="https://kronos.devxhouse.com" className="text-cyan-400 hover:underline">https://kronos.devxhouse.com</a>, you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of the Terms, you may not access the Service.</p>
          <p className="mt-3">These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 16 years old and have the legal capacity to enter into these Terms.</p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">2.</span> Description of Service
          </h2>
          <p className="mb-3">Kronos AI provides the following services:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li><strong className="text-white">Social Media Automation</strong>: AI-powered content generation, scheduling, and publishing</li>
            <li><strong className="text-white">Proposal Generation</strong>: AI-assisted creation of business proposals for freelancers</li>
            <li><strong className="text-white">AI Receptionist</strong>: Automated phone answering and customer service</li>
            <li><strong className="text-white">Document Generation</strong>: AI-powered business document creation (Fable AI)</li>
            <li><strong className="text-white">Job Discovery</strong>: Aggregation of freelance job opportunities</li>
            <li><strong className="text-white">Team Collaboration</strong>: Multi-user workspace features</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">3.</span> User Accounts
          </h2>
          <h3 className="text-white font-semibold mb-2">3.1 Account Creation</h3>
          <p className="mb-3">To use certain features, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.</p>

          <h3 className="text-white font-semibold mb-2">3.2 Account Security</h3>
          <p className="mb-3">You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.</p>

          <h3 className="text-white font-semibold mb-2">3.3 Account Termination</h3>
          <p>We reserve the right to suspend or terminate your account at any time for violation of these Terms, fraudulent activity, or any other reason at our sole discretion.</p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">4.</span> Subscriptions and Payments
          </h2>
          <h3 className="text-white font-semibold mb-2">4.1 Subscription Plans</h3>
          <p className="mb-3">Kronos AI offers various subscription plans (Free, Professional, Business, Enterprise) with different features and limits. Plan details and pricing are available on our website.</p>

          <h3 className="text-white font-semibold mb-2">4.2 Payment Processing</h3>
          <p className="mb-3">Payments are processed through <strong className="text-white">Paddle</strong>, our Merchant of Record. By subscribing to a paid plan, you agree to Paddle's terms of service. All prices are in USD unless otherwise stated.</p>

          <h3 className="text-white font-semibold mb-2">4.3 Billing Cycle</h3>
          <p className="mb-3">Subscriptions are billed monthly or annually, depending on your selection. Your subscription will automatically renew unless cancelled before the renewal date.</p>

          <h3 className="text-white font-semibold mb-2">4.4 Refunds</h3>
          <p>Due to the digital nature of our services, refunds are generally not provided after services have been rendered. However, if you experience technical issues that prevent you from using the Service, please contact us within 7 days of your payment.</p>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">5.</span> Acceptable Use Policy
          </h2>
          <p className="mb-3">You agree NOT to use the Service to:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Generate, distribute, or publish spam, malware, or malicious content</li>
            <li>Impersonate any person or entity or misrepresent your affiliation</li>
            <li>Harass, abuse, or harm another person or entity</li>
            <li>Generate content that is illegal, harmful, threatening, abusive, defamatory, or obscene</li>
            <li>Attempt to gain unauthorized access to the Service or its systems</li>
            <li>Use automated scripts to collect information or interact with the Service without permission</li>
            <li>Interfere with or disrupt the Service or servers connected to the Service</li>
            <li>Resell, sublicense, or redistribute the Service without authorization</li>
            <li>Use the AI Receptionist for fraudulent or deceptive purposes</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">6.</span> Intellectual Property
          </h2>
          <h3 className="text-white font-semibold mb-2">6.1 Our Intellectual Property</h3>
          <p className="mb-3">The Service, including its original content, features, and functionality, is owned by DevX House and protected by international copyright, trademark, and other intellectual property laws.</p>

          <h3 className="text-white font-semibold mb-2">6.2 Your Content</h3>
          <p className="mb-3">You retain ownership of content you create using the Service. By using the Service, you grant us a non-exclusive, worldwide, royalty-free license to use, store, and process your content solely for the purpose of providing the Service.</p>

          <h3 className="text-white font-semibold mb-2">6.3 AI-Generated Content</h3>
          <p>Content generated by our AI tools is provided for your use. You are responsible for reviewing and ensuring AI-generated content is appropriate before publishing or distributing it.</p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">7.</span> Third-Party Services
          </h2>
          <p className="mb-3">The Service integrates with third-party platforms and services including:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300 mb-4">
            <li>Social media platforms (Twitter/X, Facebook, LinkedIn, Instagram)</li>
            <li>AI providers (Google Gemini, OpenAI, Anthropic, Groq)</li>
            <li>Payment processors (Paddle)</li>
            <li>Communication services (Twilio, Retell AI, Vapi)</li>
          </ul>
          <p>Your use of these third-party services is subject to their respective terms of service and privacy policies. We are not responsible for the content, policies, or practices of any third-party services.</p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">8.</span> AI Receptionist Terms
          </h2>
          <p className="mb-3">If you use the AI Receptionist feature, you additionally agree to:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Provide accurate business information for the AI to use</li>
            <li>Comply with all applicable telecommunications and consumer protection laws</li>
            <li>Inform callers when required by law that they are interacting with an AI assistant</li>
            <li>Not use the AI Receptionist for fraudulent, deceptive, or illegal purposes</li>
            <li>Take responsibility for any actions or commitments made by the AI on your behalf</li>
            <li>Monitor and review AI interactions to ensure accuracy and appropriateness</li>
          </ul>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">9.</span> Disclaimers
          </h2>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
            <p className="text-yellow-300 font-semibold">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</p>
          </div>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>We do not guarantee that the Service will be uninterrupted, secure, or error-free</li>
            <li>AI-generated content may contain errors, inaccuracies, or inappropriate material</li>
            <li>We do not guarantee the accuracy of job listings or third-party data</li>
            <li>The AI Receptionist may not handle all situations appropriately</li>
            <li>Social media posting may fail due to platform API changes or restrictions</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">10.</span> Limitation of Liability
          </h2>
          <p className="mb-3">To the maximum extent permitted by law:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>DevX House shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
            <li>Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim</li>
            <li>We are not liable for any loss of data, revenue, profits, or business opportunities</li>
            <li>We are not liable for actions taken by the AI Receptionist on your behalf</li>
          </ul>
        </section>

        {/* Section 11 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">11.</span> Indemnification
          </h2>
          <p>You agree to indemnify, defend, and hold harmless DevX House, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of your use of the Service, your violation of these Terms, or your violation of any rights of another party.</p>
        </section>

        {/* Section 12 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">12.</span> Governing Law
          </h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of Pakistan, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved through good-faith negotiation, and if unsuccessful, through binding arbitration in Lahore, Pakistan.</p>
        </section>

        {/* Section 13 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">13.</span> Changes to Terms
          </h2>
          <p>We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
        </section>

        {/* Section 14 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">14.</span> Severability
          </h2>
          <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-cyan-400">15.</span> Contact Us
          </h2>
          <p className="mb-4">If you have questions about these Terms and Conditions:</p>
          <div className="space-y-2">
            <p><span className="text-white font-semibold">Kronos AI by DevX House</span></p>
            <p>📧 <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:underline">kronos@devxhouse.com</a></p>
            <p>🌐 <a href="https://kronos.devxhouse.com" className="text-cyan-400 hover:underline">https://kronos.devxhouse.com</a></p>
          </div>
        </section>

        <p className="text-center text-gray-500 text-sm pt-4 border-t border-white/10">
          These Terms and Conditions were last updated on June 15, 2026.
        </p>
      </div>
    </div>
  );
}
